import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabaseClient';

const LoginPage = () => {
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite');

  const [mode, setMode] = useState(inviteCode ? 'signup' : 'login'); // 'login', 'signup', or 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [parentInviteInfo, setParentInviteInfo] = useState(null);
  const [validatingInvite, setValidatingInvite] = useState(false);
  const [resetEmailSent, setResetEmailSent] = useState(false);

  const { signIn, signUp } = useAuth();
  const navigate = useNavigate();

  // Show error if trying to access signup without invite
  useEffect(() => {
    if (mode === 'signup' && !inviteCode) {
      setError('Signup requires an invite code. Please contact your coach or administrator.');
    } else {
      setError('');
    }
  }, [mode, inviteCode]);

  // Validate parent invite and fetch player info
  useEffect(() => {
    const validateParentInvite = async () => {
      if (!inviteCode || mode !== 'signup') return;

      setValidatingInvite(true);
      try {
        const { data, error } = await supabase.rpc('validate_parent_invite_code', {
          invite_code: inviteCode,
        });

        if (error) throw error;

        if (data && data.length > 0 && data[0].valid) {
          console.log('Parent invite validation result:', data[0]);

          // Use player details from validation function (bypasses RLS)
          const players = data[0].players || [];

          console.log('Players from validation:', players);

          setParentInviteInfo({
            players: players,
            parentEmail: data[0].parent_email,
          });

          // Pre-fill email if provided in invite
          if (data[0].parent_email && !email) {
            setEmail(data[0].parent_email);
          }
        }
      } catch (err) {
        console.error('Error validating parent invite:', err);
        // Not a parent invite, could be regular invite - that's okay
      } finally {
        setValidatingInvite(false);
      }
    };

    validateParentInvite();
  }, [inviteCode, mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      if (mode === 'login') {
        const { error } = await signIn(email, password);
        if (error) throw error;
        navigate('/');
      } else {
        // Signup requires invite code
        if (!inviteCode) {
          throw new Error('Signup requires an invite code');
        }

        const { error } = await signUp(email, password, {
          full_name: fullName,
          invite_code: inviteCode,
        });
        if (error) throw error;
        setMessage('Account created! Please check your email to verify your account.');
        setMode('login');
        setPassword('');
      }
    } catch (error) {
      // Provide user-friendly error messages
      let errorMessage = error.message;

      if (
        errorMessage.includes('duplicate key value violates unique constraint "profiles_email_key"')
      ) {
        errorMessage =
          'This email address is already registered. Please log in instead or contact your coach if you believe this is an error.';
      } else if (errorMessage.includes('Database error')) {
        errorMessage =
          'Unable to create your account. This email may already be registered. Please try logging in, or contact support if you continue to have issues.';
      } else if (errorMessage.includes('User already registered')) {
        errorMessage =
          'This email address is already registered. Please use the login form instead.';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      setResetEmailSent(true);
      setMessage('Password reset email sent! Check your inbox for a link to reset your password.');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5',
      padding: '20px',
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
      padding: '40px',
      width: '100%',
      maxWidth: '400px',
    },
    header: {
      textAlign: 'center',
      marginBottom: '30px',
    },
    logo: {
      maxWidth: '450px',
      width: '100%',
      height: 'auto',
      marginBottom: '20px',
    },
    title: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#333',
      margin: '0 0 10px 0',
    },
    subtitle: {
      fontSize: '14px',
      color: '#666',
      margin: 0,
    },
    tabs: {
      display: 'flex',
      gap: '10px',
      marginBottom: '30px',
      borderBottom: '1px solid #e0e0e0',
    },
    tab: {
      flex: 1,
      padding: '10px',
      border: 'none',
      background: 'none',
      cursor: 'pointer',
      fontSize: '16px',
      fontWeight: '500',
      color: '#666',
      borderBottom: '2px solid transparent',
      transition: 'all 0.2s',
    },
    activeTab: {
      color: '#007bff',
      borderBottomColor: '#007bff',
    },
    form: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    label: {
      fontSize: '14px',
      fontWeight: '500',
      color: '#333',
    },
    input: {
      padding: '12px',
      fontSize: '14px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      outline: 'none',
      transition: 'border-color 0.2s',
    },
    button: {
      padding: '12px',
      fontSize: '16px',
      fontWeight: '500',
      color: 'white',
      backgroundColor: '#007bff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    buttonDisabled: {
      backgroundColor: '#ccc',
      cursor: 'not-allowed',
    },
    error: {
      padding: '12px',
      backgroundColor: '#fee',
      color: '#c33',
      borderRadius: '4px',
      fontSize: '14px',
      border: '1px solid #fcc',
    },
    success: {
      padding: '12px',
      backgroundColor: '#efe',
      color: '#3c3',
      borderRadius: '4px',
      fontSize: '14px',
      border: '1px solid #cfc',
    },
    inviteInfo: {
      padding: '12px',
      backgroundColor: '#e3f2fd',
      color: '#1976d2',
      borderRadius: '4px',
      fontSize: '14px',
      border: '1px solid #90caf9',
      marginBottom: '20px',
      textAlign: 'center',
    },
    linkButton: {
      background: 'none',
      border: 'none',
      color: '#007bff',
      cursor: 'pointer',
      textDecoration: 'underline',
      fontSize: '14px',
      padding: '0',
      marginTop: '10px',
    },
    textCenter: {
      textAlign: 'center',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <img src="/logo.png" alt="Reps Over Rings Logo" style={styles.logo} />
          <p style={styles.subtitle}>Baseball Player Development</p>
        </div>

        {mode !== 'reset' && (
          <div style={styles.tabs}>
            <button
              style={{
                ...styles.tab,
                ...(mode === 'login' ? styles.activeTab : {}),
              }}
              onClick={() => {
                setMode('login');
                setError('');
                setMessage('');
                setResetEmailSent(false);
              }}
            >
              Login
            </button>
            <button
              style={{
                ...styles.tab,
                ...(mode === 'signup' ? styles.activeTab : {}),
                ...(!inviteCode ? styles.buttonDisabled : {}),
              }}
              onClick={() => {
                if (inviteCode) {
                  setMode('signup');
                  setError('');
                  setMessage('');
                  setResetEmailSent(false);
                }
              }}
              disabled={!inviteCode}
              title={!inviteCode ? 'Invite code required to sign up' : ''}
            >
              Sign Up
            </button>
          </div>
        )}

        {inviteCode && mode === 'signup' && (
          <div style={styles.inviteInfo}>
            <strong>Invite Code:</strong> {inviteCode}
          </div>
        )}

        {validatingInvite && mode === 'signup' && (
          <div style={styles.inviteInfo}>
            <em>Validating invite...</em>
          </div>
        )}

        {parentInviteInfo && mode === 'signup' && (
          <div style={{ ...styles.inviteInfo, backgroundColor: '#e8f5e9', borderColor: '#4caf50' }}>
            <strong>Parent Invite:</strong> You're joining as a parent for:
            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              {parentInviteInfo.players.map((player, index) => (
                <li key={index}>
                  <strong>
                    {player.first_name} {player.last_name}
                  </strong>
                  {player.jersey_number && ` (#${player.jersey_number})`}
                </li>
              ))}
            </ul>
          </div>
        )}

        {error && <div style={styles.error}>{error}</div>}
        {message && <div style={styles.success}>{message}</div>}

        {mode === 'reset' ? (
          // Password Reset Form
          <>
            <div style={{ ...styles.header, marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', margin: '0 0 8px 0' }}>Reset Your Password</h2>
              <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
                Enter your email address and we'll send you a link to reset your password.
              </p>
            </div>

            {!resetEmailSent ? (
              <form onSubmit={handlePasswordReset} style={styles.form}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Email</label>
                  <input
                    type="email"
                    style={styles.input}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                  />
                </div>

                <button
                  type="submit"
                  style={{
                    ...styles.button,
                    ...(loading ? styles.buttonDisabled : {}),
                  }}
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>

                <div style={styles.textCenter}>
                  <button
                    type="button"
                    style={styles.linkButton}
                    onClick={() => {
                      setMode('login');
                      setError('');
                      setMessage('');
                      setResetEmailSent(false);
                    }}
                  >
                    Back to Login
                  </button>
                </div>
              </form>
            ) : (
              <div style={styles.textCenter}>
                <p style={{ marginBottom: '20px', color: '#666' }}>
                  Check your email for a password reset link.
                </p>
                <button
                  type="button"
                  style={styles.button}
                  onClick={() => {
                    setMode('login');
                    setError('');
                    setMessage('');
                    setResetEmailSent(false);
                  }}
                >
                  Back to Login
                </button>
              </div>
            )}
          </>
        ) : (
          // Login/Signup Form
          <form onSubmit={handleSubmit} style={styles.form}>
            {mode === 'signup' && (
              <div style={styles.formGroup}>
                <label style={styles.label}>Full Name</label>
                <input
                  type="text"
                  style={styles.input}
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Enter your full name"
                  required
                />
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                style={styles.input}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Password</label>
              <input
                type="password"
                style={styles.input}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                minLength={6}
              />
            </div>

            <button
              type="submit"
              style={{
                ...styles.button,
                ...(loading ? styles.buttonDisabled : {}),
              }}
              disabled={loading}
            >
              {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
            </button>

            {mode === 'login' && (
              <div style={styles.textCenter}>
                <button
                  type="button"
                  style={styles.linkButton}
                  onClick={() => {
                    setMode('reset');
                    setError('');
                    setMessage('');
                  }}
                >
                  Forgot Password?
                </button>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
